<script setup lang="ts">
import {onMounted, ref, nextTick, computed, watch} from "vue";
import AboutUsHero from "@/pages/AboutUs/AboutUsHero.vue";
import {VueAwesomePaginate} from "vue-awesome-paginate";
import {useWindowSize} from "@vueuse/core";
import ArticleCard from "@/components/ArticleCard/ArticleCard.vue";
import SearchArticles from "@/components/SearchArticles/SearchArticles.vue"; // Ensure nextTick is imported
import { useBlogStore } from "@/stores/blogStore";
import {useRoute} from "vue-router";
import {useArticleStore} from "@/stores/articleStore";
import {VueSpinner} from "vue3-spinners";
import {ArrowDownBold, ArrowUpBold} from "@element-plus/icons-vue";



const blogStore = useBlogStore();
const articleStore = useArticleStore();

const slug = "travel-inspiration";

const route = useRoute();


const { width } = useWindowSize();
const isMobileSmall = computed(() => width.value < 1024);
const isSortOpen = ref(false)


const heroImage = computed(() => {
  const imageSet = blogStore.allArticles[0]?.imageSet
  if (!imageSet) return ''

  return imageSet['1600w'] || Object.values(imageSet)[0] || ''
})

onMounted(() => {
  blogStore.setFromQuery(route.query);
  blogStore.getAllArticles(slug, blogStore.currentPage, blogStore.articlesPerPage);

});

// Watch route query changes
watch(() => route.query, (newQuery) => {
  blogStore.setFromQuery(newQuery);
});

watch(() => blogStore.currentPage, () => {
  nextTick(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
});


</script>

<template>
  <!-- Hero Section -->
  <div v-if="blogStore.loading" class="grid place-content-center mt-24 h-[70vh]">
    <VueSpinner size="30" color="#beA5aa" />
  </div>
  <section
      class="container relative w-full h-[600px] md:h-[1000px] bg-cover bg-center bg-no-repeat"
      :style="{ backgroundImage: `url('${heroImage}')` }"
  >
    <div class="absolute bottom-10 left-4 text-white max-w-3xl">
      <h1 class="responsive-title-3 font-bold mt-4">
        {{ blogStore.allArticles[0]?.title }}
      </h1>
    </div>
  </section>

  <!-- Sort Dropdown -->
  <div class="container flex justify-end mt-6">

    <el-dropdown trigger="click" @visible-change="val => isSortOpen = val">
      <span class="el-dropdown-link flex items-center cursor-pointer responsive-body-medium">
        <span class="sort-label text-bw-midGrey font-semibold">
          Sort by: {{ blogStore.selectedSort }}
        </span>
        <el-icon class="el-icon--right">
          <component
              :is="isSortOpen ? ArrowUpBold : ArrowDownBold"
              :style="{ width: '12px', height: '12px' }"
          />

        </el-icon>
      </span>
      <template #dropdown>
        <el-dropdown-menu>
          <el-dropdown-item
              v-for="option in blogStore.sortOptions"
              :key="option"
              @click="() => {
                blogStore.selectedSort = option;
                blogStore.updateRouteQuery();
              }"
          >
            {{ option }}
          </el-dropdown-item>
        </el-dropdown-menu>
      </template>
    </el-dropdown>
  </div>
  <!-- Content -->
  <div class="container pb-12 mt-6 place-items-center">

    <!-- Blog Posts -->
    <div class="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">

      <div
          v-for="page in blogStore.filteredArticles"
          :key="page.id"
      >
        <ArticleCard
            :page="page"
            @on-visit-article="articleStore.visitArticle(page.slug)"
        />
      </div>
    </div>
    <!-- Pagination -->

    <div v-show="blogStore.totalPages>0" class="mt-15 flex justify-center">
      <VueAwesomePaginate
          :total-items="blogStore.totalItems"
          :items-per-page="blogStore.articlesPerPage"
          :max-pages-shown="isMobileSmall ? 1 : 3"
          v-model="blogStore.currentPage"
      />
    </div>
  </div>
</template>

<style scoped>
:deep(.el-dropdown-menu__item){
  font-size: 15px;
  margin-bottom: 1px;
  color: #676188 !important;
}

@media (max-width: 768px) {
  :deep(.el-dropdown-menu__item){
    font-size: 15px;
    color: #676188 !important;
  }
}

:deep(.el-input__inner) {
  font-size: 25px;
  height: 70px;
  line-height: 48px;
  padding-top: 0;
  padding-bottom: 0;
  color: #beA5aa;
  --el-input-placeholder-color: #beA5aa;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
